import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Trash2,
  Check,
  X as XIcon,
  Edit2,
  GripVertical,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Tooltip } from '../ui/tooltip';
import { toast } from 'sonner';
import {
  GET_PROJECT,
  GET_PROJECT_COLUMNS,
  GET_PROJECT_TASKS,
  CREATE_COLUMN_MUTATION,
  UPDATE_COLUMN_MUTATION,
  DELETE_COLUMN_MUTATION,
  REORDER_COLUMNS_MUTATION,
} from '../../graphql/queries';

interface Column {
  id: string;
  name: string;
  color?: string | null;
  order: number;
  isTerminal: boolean;
}

interface ProjectColumnsEditorProps {
  projectId: string;
  /** Disable all editing UI when false. */
  canEdit?: boolean;
}

interface SortableColumnRowProps {
  column: Column;
  taskCount: number;
  canEdit: boolean;
  onSave: (
    id: string,
    name: string,
    color: string,
    isTerminal: boolean,
  ) => Promise<void>;
  onDelete: (column: Column) => void;
}

const TERMINAL_TOOLTIP =
  'Zadania w kolumnach oznaczonych jako końcowe są wliczane do procentu wykonania projektu. Możesz mieć kilka kolumn końcowych (np. „Zrobione" i „Zatwierdzone").';

const SortableColumnRow: React.FC<SortableColumnRowProps> = ({
  column,
  taskCount,
  canEdit,
  onSave,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(column.name);
  const [color, setColor] = useState<string>(column.color || '#168dc8');
  const [isTerminal, setIsTerminal] = useState<boolean>(column.isTerminal);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) {
      setName(column.name);
      setColor(column.color || '#168dc8');
      setIsTerminal(column.isTerminal);
    }
  }, [column.name, column.color, column.isTerminal, editing]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: { type: 'column-edit', columnId: column.id },
    disabled: !canEdit,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Nazwa kolumny jest wymagana');
      return;
    }
    setSaving(true);
    try {
      await onSave(column.id, name.trim(), color, isTerminal);
      setEditing(false);
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się zapisać kolumny');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-background border border-border rounded-md"
    >
      {canEdit && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1"
          aria-label="Przeciągnij kolumnę"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}

      {editing ? (
        <>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 h-8"
            maxLength={100}
            autoFocus
          />
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-8 w-10 rounded border border-input cursor-pointer"
            title="Kolor kolumny"
          />
          <label className="flex items-center gap-1.5 text-xs select-none cursor-pointer">
            <input
              type="checkbox"
              checked={isTerminal}
              onChange={(e) => setIsTerminal(e.target.checked)}
              className="h-3.5 w-3.5 accent-primary cursor-pointer"
            />
            <span>Końcowa</span>
            <Tooltip content={<div className="max-w-xs">{TERMINAL_TOOLTIP}</div>} side="top">
              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
            </Tooltip>
          </label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleSave}
            disabled={saving}
            className="h-8"
          >
            <Check className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="h-8"
          >
            <XIcon className="w-4 h-4" />
          </Button>
        </>
      ) : (
        <>
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: column.color || 'transparent' }}
          />
          <span className="flex-1 text-sm font-medium">{column.name}</span>
          <span className="text-xs text-muted-foreground">
            {taskCount} {taskCount === 1 ? 'zadanie' : 'zadań'}
          </span>
          {column.isTerminal && (
            <span className="text-[10px] uppercase text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              końcowa
            </span>
          )}
          {canEdit && (
            <>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(true)}
                className="h-8 w-8 p-0"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onDelete(column)}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export const ProjectColumnsEditor: React.FC<ProjectColumnsEditorProps> = ({
  projectId,
  canEdit = true,
}) => {
  const { data: columnsData, refetch: refetchColumns } = useQuery<{
    projectColumns: Column[];
  }>(GET_PROJECT_COLUMNS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: tasksData } = useQuery<{
    projectTasks: Array<{ id: string; columnId: string }>;
  }>(GET_PROJECT_TASKS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const [createColumn] = useMutation(CREATE_COLUMN_MUTATION, {
    refetchQueries: [
      { query: GET_PROJECT_COLUMNS, variables: { projectId } },
    ],
    awaitRefetchQueries: true,
  });
  const [updateColumn] = useMutation(UPDATE_COLUMN_MUTATION, {
    refetchQueries: [
      { query: GET_PROJECT_COLUMNS, variables: { projectId } },
      { query: GET_PROJECT, variables: { id: projectId } },
    ],
    awaitRefetchQueries: true,
  });
  const [deleteColumn, { loading: deleting }] = useMutation(
    DELETE_COLUMN_MUTATION,
    {
      refetchQueries: [
        { query: GET_PROJECT_COLUMNS, variables: { projectId } },
        { query: GET_PROJECT_TASKS, variables: { projectId } },
        { query: GET_PROJECT, variables: { id: projectId } },
      ],
      awaitRefetchQueries: true,
    },
  );
  const [reorderColumns] = useMutation(REORDER_COLUMNS_MUTATION);

  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#168dc8');
  const [newIsTerminal, setNewIsTerminal] = useState(false);
  const [creating, setCreating] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<Column | null>(null);
  const [moveTasksTo, setMoveTasksTo] = useState<string>('');

  const columns = useMemo(() => {
    return [...(columnsData?.projectColumns || [])].sort(
      (a, b) => a.order - b.order,
    );
  }, [columnsData]);

  const taskCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    (tasksData?.projectTasks || []).forEach((t) => {
      counts[t.columnId] = (counts[t.columnId] || 0) + 1;
    });
    return counts;
  }, [tasksData]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = columns.map((c) => c.id);
    const fromIdx = ids.indexOf(String(active.id));
    const toIdx = ids.indexOf(String(over.id));
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, String(active.id));
    try {
      await reorderColumns({
        variables: {
          input: { projectId, orderedColumnIds: next },
        },
        refetchQueries: [
          { query: GET_PROJECT_COLUMNS, variables: { projectId } },
        ],
        awaitRefetchQueries: true,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się zmienić kolejności kolumn');
      refetchColumns().catch(() => {});
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Nazwa kolumny jest wymagana');
      return;
    }
    setCreating(true);
    try {
      await createColumn({
        variables: {
          input: {
            projectId,
            name: newName.trim(),
            color: newColor,
            isTerminal: newIsTerminal,
          },
        },
      });
      toast.success('Kolumna dodana');
      setNewName('');
      setNewColor('#168dc8');
      setNewIsTerminal(false);
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się dodać kolumny');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveRow = async (
    id: string,
    name: string,
    color: string,
    isTerminal: boolean,
  ) => {
    await updateColumn({
      variables: { id, input: { name, color, isTerminal } },
    });
    toast.success('Kolumna zaktualizowana');
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const taskCount = taskCounts[confirmDelete.id] || 0;
    if (taskCount > 0 && !moveTasksTo) {
      toast.error('Wybierz kolumnę, do której chcesz przenieść zadania');
      return;
    }
    try {
      await deleteColumn({
        variables: {
          id: confirmDelete.id,
          moveTasksToColumnId: taskCount > 0 ? moveTasksTo : null,
        },
      });
      toast.success('Kolumna usunięta');
      setConfirmDelete(null);
      setMoveTasksTo('');
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się usunąć kolumny');
    }
  };

  const otherColumns = useMemo(() => {
    if (!confirmDelete) return [];
    return columns.filter((c) => c.id !== confirmDelete.id);
  }, [columns, confirmDelete]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Kolumny tablicy Kanban</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!canEdit && (
          <p className="text-sm text-muted-foreground">
            Brak uprawnień do konfiguracji tablicy. Wymagane uprawnienie:
            kanban.configure.
          </p>
        )}

        {columns.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Brak kolumn. Dodaj pierwszą poniżej.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columns.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {columns.map((c) => (
                  <SortableColumnRow
                    key={c.id}
                    column={c}
                    taskCount={taskCounts[c.id] || 0}
                    canEdit={canEdit}
                    onSave={handleSaveRow}
                    onDelete={(col) => {
                      setConfirmDelete(col);
                      setMoveTasksTo('');
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {canEdit && (
          <div className="border-t border-border pt-3">
            <Label className="text-xs uppercase text-muted-foreground">
              Nowa kolumna
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nazwa kolumny"
                className="flex-1"
                maxLength={100}
              />
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="h-10 w-12 rounded border border-input cursor-pointer"
                title="Kolor kolumny"
              />
              <Button
                type="button"
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
              >
                <Plus className="w-4 h-4 mr-1" />
                Dodaj
              </Button>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 select-none cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={newIsTerminal}
                onChange={(e) => setNewIsTerminal(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary cursor-pointer"
              />
              <span>Końcowa</span>
              <Tooltip content={<div className="max-w-xs">{TERMINAL_TOOLTIP}</div>} side="top">
                <Info className="w-3.5 h-3.5 cursor-help" />
              </Tooltip>
            </label>
          </div>
        )}
      </CardContent>

      {/* Confirm delete */}
      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(v) => {
          if (!v) {
            setConfirmDelete(null);
            setMoveTasksTo('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Usuń kolumnę
            </DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć kolumnę{' '}
              <strong>{confirmDelete?.name}</strong>? Tej operacji nie można
              cofnąć.
            </DialogDescription>
          </DialogHeader>

          {confirmDelete &&
            (taskCounts[confirmDelete.id] || 0) > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Kolumna zawiera{' '}
                  <strong>{taskCounts[confirmDelete.id]}</strong> zadań.
                  Wybierz, do której kolumny je przenieść:
                </p>
                <Select value={moveTasksTo} onValueChange={setMoveTasksTo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wybierz kolumnę docelową" />
                  </SelectTrigger>
                  <SelectContent>
                    {otherColumns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDelete(null);
                setMoveTasksTo('');
              }}
              disabled={deleting}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
